import type { ReploIntegration } from "schemas/generated/integration";
import type { z } from "zod";

import * as React from "react";

import Input from "@common/designSystem/Input";
import DeleteConfirmationModal from "@editor/components/common/DeleteConfirmationModal";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  errorToast,
  successToast,
} from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { useForm } from "react-hook-form";

import { INTEGRATION_CONFIGS } from "./config";

export type IntegrationType = Exclude<
  ReploIntegration["type"],
  "ShopifyIntegrationConfig"
>;

type IntegrationFormData<T extends IntegrationType> = z.infer<
  (typeof INTEGRATION_CONFIGS)[T]["schema"]
>;

export const IntegrationForm: React.FC<{
  integration: IntegrationType;
  handleCancel: () => void;
  initialValues?: IntegrationFormData<IntegrationType>;
  integrationId?: string;
}> = ({ integration, handleCancel, initialValues, integrationId }) => {
  const { title, description, image } = INTEGRATION_CONFIGS[integration];

  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();

  const doesIntegrationExist = Boolean(integrationId);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const { mutate: createIntegration, isPending: isCreatingIntegration } =
    trpc.integration.createOrUpdate.useMutation({
      onSuccess: () => {
        if (doesIntegrationExist) {
          successToast(
            "Integration Updated",
            "Your integration has been updated successfully.",
          );
        } else {
          successToast(
            "Integration Created",
            "Your integration has been created successfully.",
          );
        }

        void trpcUtils.integration.find.invalidate();
        void trpcUtils.integration.get.invalidate();
        handleCancel();
      },
      onError: () => {
        if (doesIntegrationExist) {
          errorToast(
            "Error Updating Integration",
            "There was an error updating your integration.",
          );
        } else {
          errorToast(
            "Error Creating Integration",
            "There was an error creating your integration.",
          );
        }
      },
    });

  const { mutate: deleteIntegration, isPending: isDeletingIntegration } =
    trpc.integration.deleteIntegrationFromProject.useMutation({
      onSuccess: () => {
        successToast(
          "Integration Disconnected",
          "Your integration has been disconnected successfully.",
        );
        // Navigate immediately
        handleCancel();
        // Only invalidate the list query to refresh the integrations list
        // Don't invalidate integration.get since we just deleted it
        void trpcUtils.integration.find.invalidate();
      },
      onError: () => {
        errorToast(
          "Error Disconnecting Integration",
          "There was an error disconnecting your integration.",
        );
      },
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IntegrationFormData<typeof integration>>({
    resolver: zodResolver(INTEGRATION_CONFIGS[integration].schema),
    defaultValues: initialValues,
  });

  const onSubmit = handleSubmit(
    (data: IntegrationFormData<typeof integration>) => {
      createIntegration({
        type: integration,
        config: data,
        workspaceId: workspaceId ?? "",
        projectId: projectId ?? "",
        id: integrationId,
      });
    },
  );

  /**
   * NOTE (Max, 2025-07-31): The fields on the IntegrationForm are dynamically generated based on the integration type.
   * That's because the fields required for Klaviyo are different from e.g. Hubspot. To figure out which fields
   * to generate, we look at INTEGRATION_CONFIGS.
   *
   * We use the functions provided by useForm to register the fields and show errors.
   */
  const renderFields = () => {
    const fields = INTEGRATION_CONFIGS[integration].fields;
    return (
      <>
        {Object.entries(fields).map(([fieldName, fieldConfig]) => {
          const errorMessage = errors[fieldName as keyof typeof errors];

          return (
            <div key={fieldName} className="space-y-1">
              <span className="text-sm font-medium ">{fieldConfig.label}</span>
              <Input
                {...register(
                  fieldName as keyof IntegrationFormData<typeof integration>,
                )}
                placeholder={fieldConfig.placeholder}
                size="base"
              />
              {errorMessage && (
                <p className="text-sm text-danger">{errorMessage.message}</p>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="border-0.5 border-border p-4 rounded-xl flex flex-col gap-4">
      <div className="flex flex-row gap-3">
        <img
          src={image}
          alt={title}
          style={{
            width: "36px",
            height: "36px",
          }}
          className="contain rounded-sm"
        />
        <div className="flex flex-col">
          <span className="header-h4">{title}</span>
          <span className="text-base text-muted">{description}</span>
        </div>
      </div>

      <div className="space-y-4">{renderFields()}</div>

      <div className="flex flex-row justify-between gap-3">
        {doesIntegrationExist && (
          <Button
            variant="secondary"
            size="base"
            onClick={() => setShowDeleteModal(true)}
          >
            Disconnect
          </Button>
        )}
        <div className="flex flex-row gap-3">
          <Button variant="secondary" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="base"
            isLoading={isCreatingIntegration}
            onClick={() => void onSubmit()}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmationModal
          onDelete={() => {
            deleteIntegration({
              projectId: projectId ?? "",
              integrationId: integrationId ?? "",
            });
            setShowDeleteModal(false);
          }}
          onRequestClose={() => setShowDeleteModal(false)}
          assetName={title}
          assetTypeDisplayName="Integration"
          isLoading={isDeletingIntegration}
          extraMessage="This action cannot be undone."
        />
      )}
    </div>
  );
};
